import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import type { ComposerStyles } from "./composerStyles";
import type { CloneWeekUiResult } from "./useComposer";

// ⧉ Clone-week control (Antenore 2026-06-11, the 5/3/1 vertical flow:
// fill W1 across the days, clone it onto W2..Wn, then ramp each week
// with the per-week edit). Collapsed to one row; open → source chip
// (single) + target chips (multi) + Apply. Refuses cleanly when a
// target week already has content — never half-clones.
export function CloneWeekBar({
  weekCount,
  resolveName,
  onClone,
  styles,
}: {
  weekCount: number;
  resolveName: (exerciseId: string, snapshot?: string) => string;
  onClone: (fromWeek: number, toWeeks: number[]) => CloneWeekUiResult;
  styles: ComposerStyles;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(0);
  const [targets, setTargets] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  if (weekCount < 2) return null;

  const toggleTarget = (w: number) => {
    setError(null);
    setTargets((cur) => {
      const next = new Set(cur);
      if (next.has(w)) next.delete(w);
      else next.add(w);
      return next;
    });
  };

  const apply = () => {
    const r = onClone(from, [...targets]);
    if (r.ok) {
      setOpen(false);
      setTargets(new Set());
      setError(null);
      return;
    }
    if (r.reason === "conflict") {
      setError(
        t("composer.cloneWeekConflict", {
          name: resolveName(r.conflict.exerciseId),
          week: r.conflict.week + 1,
          day: r.conflict.dayIdx + 1,
          defaultValue:
            "{{name}} already has content in W{{week}} (D{{day}})",
        }),
      );
      return;
    }
    setError(
      t("composer.cloneWeekEmpty", {
        defaultValue: "Nothing to clone — pick a filled source week and at least one target",
      }),
    );
  };

  return (
    <View style={{ gap: 8, marginTop: 4 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        onPress={() => {
          setOpen((o) => !o);
          setError(null);
        }}
        style={styles.addExercisesBtn}
      >
        <Text style={styles.addExercisesText}>
          ⧉ {t("composer.cloneWeek", { defaultValue: "Clone week…" })}
        </Text>
      </Pressable>
      {open ? (
        <>
          <View style={styles.chipsLine}>
            <Text style={styles.chipsLabel}>
              {t("composer.cloneWeekFrom", { defaultValue: "From" })}
            </Text>
            <View style={styles.chipsRow}>
              {Array.from({ length: weekCount }, (_, w) => (
                <Pressable
                  key={w}
                  accessibilityRole="button"
                  accessibilityState={{ selected: from === w }}
                  onPress={() => {
                    setFrom(w);
                    setError(null);
                    setTargets((cur) => {
                      const next = new Set(cur);
                      next.delete(w);
                      return next;
                    });
                  }}
                  hitSlop={8}
                  style={[styles.chip, from === w && styles.chipOn]}
                >
                  <Text style={[styles.chipText, from === w && styles.chipTextOn]}>
                    W{w + 1}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
          <View style={styles.chipsLine}>
            <Text style={styles.chipsLabel}>
              {t("composer.cloneDayTarget", { defaultValue: "To" })}
            </Text>
            <View style={styles.chipsRow}>
              {Array.from({ length: weekCount }, (_, w) => {
                if (w === from) return null;
                const on = targets.has(w);
                return (
                  <Pressable
                    key={w}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                    onPress={() => toggleTarget(w)}
                    hitSlop={8}
                    style={[styles.chip, on && styles.chipOn]}
                  >
                    <Text style={[styles.chipText, on && styles.chipTextOn]}>
                      {on ? "✓ " : ""}W{w + 1}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <Pressable
            accessibilityRole="button"
            disabled={targets.size === 0}
            onPress={apply}
            style={[styles.commitBtn, targets.size === 0 && styles.commitBtnDisabled]}
          >
            <Text
              style={[
                styles.commitBtnText,
                targets.size === 0 && styles.commitBtnTextDisabled,
              ]}
            >
              ⧉ {t("composer.cloneWeekApply", { defaultValue: "Clone" })}
            </Text>
          </Pressable>
        </>
      ) : null}
      {error ? <Text style={styles.validationText}>{error}</Text> : null}
    </View>
  );
}
